import { Locale } from 'date-fns';

// Cache for loaded locales to avoid repeated imports. Keys are normalized (lowercase, hyphen-separated)
const localeCache: Record<string, Locale> = {};

// Explicit map of locales to dynamic import functions. Keys are lowercase (e.g. 'en-gb')
const localeLoaders: Record<string, () => Promise<Locale>> = {
    'en-gb': async () => (await import('date-fns/locale/en-GB')).enGB,
    'en-bz': async () => (await import('date-fns/locale/en-GB')).enGB, // Fallback to en-GB for en-BZ
    'ar-sa': async () => (await import('date-fns/locale/ar-SA')).arSA,
    // Add supported locales here (use lowercase keys, exact import paths may keep original casing)
};

/**
 * Loads a date-fns locale dynamically with caching.
 * Falls back to 'en-gb' if loading fails or locale unsupported.
 *
 * Accepts input in many forms: 'en-GB', 'en_gb', 'en-gb', 'EN_gb', etc.
 * Normalizes to lowercase and hyphen-separated keys to perform lookups.
 *
 * @param locale - locale code like 'en-GB' or 'ar-SA'
 * @returns The loaded Locale object
 */
export async function loadDateFnsLocale(locale: string): Promise<Locale> {
    const normalized = (locale || 'en-gb').replace(/_/g, '-').toLowerCase();

    if (localeCache[normalized]) return localeCache[normalized];

    try {
        const loader = localeLoaders[normalized];
        if (!loader) throw new Error(`No loader for: ${locale}`);

        const loadedLocale = await loader();

        localeCache[normalized] = loadedLocale;
        return loadedLocale;
    } catch (err) {
        console.error(`Failed to load locale '${locale}', falling back to 'en-gb'`, err);

        const fallbackLocale = await localeLoaders['en-gb']();

        localeCache[normalized] = fallbackLocale;
        return fallbackLocale;
    }
}
